import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, PageHeader, Modal, Input } from '../../components/ui';
import api from '../../services/api';
import toast from 'react-hot-toast';

const STAGES = ['new','contacted','qualified','proposal','negotiation','won','lost'];
const STAGE_COLOR = { new:'gray', contacted:'info', qualified:'info', proposal:'warning', negotiation:'warning', won:'success', lost:'danger' };
const STAGE_LABEL = { new:'New', contacted:'Contacted', qualified:'Qualified', proposal:'Proposal', negotiation:'Negotiation', won:'Won 🎉', lost:'Lost' };

export default function CRM() {
  const [leads, setLeads]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [view, setView]           = useState('kanban');
  const [showNotes, setShowNotes] = useState(null);
  const [notes, setNotes]         = useState('');
  const [crmValue, setCrmValue]   = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try { const r = await api.get('/leads'); setLeads(r.data.data || []); }
    catch {} finally { setLoading(false); }
  };

  const moveStage = async (leadId, stage) => {
    try { await api.put(`/leads/${leadId}/notes`, { crmStage: stage }); fetchLeads(); }
    catch { toast.error('Failed to update stage'); }
  };

  const openNotes = (lead) => { setShowNotes(lead); setNotes(lead.crmNotes || ''); setCrmValue(lead.crmValue || ''); };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/leads/${showNotes.id}/notes`, { crmNotes: notes, crmValue: parseFloat(crmValue) || 0 });
      toast.success('Notes saved!'); setShowNotes(null); fetchLeads();
    } catch { toast.error('Failed'); } finally { setSavingNotes(false); }
  };

  const byStage       = (stage) => leads.filter(l => (l.crmStage || 'new') === stage);
  const totalValue    = leads.filter(l => l.crmStage === 'won').reduce((s,l) => s + (l.crmValue||0), 0);
  const pipelineValue = leads.filter(l => !['won','lost','new'].includes(l.crmStage||'new')).reduce((s,l) => s + (l.crmValue||0), 0);

  const LeadCard = ({ lead }) => (
    <div style={{ background:'var(--surface-2)', border:'1px solid var(--border-color)', borderRadius:12, padding:14, marginBottom:10, cursor:'pointer', transition:'border-color 0.15s' }}
      onClick={() => openNotes(lead)}
      onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(46,204,113,0.35)'}
      onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border-color)'}>
      <div style={{ fontWeight:700, fontSize:13, color:'var(--text-primary)', marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lead.firstName||''} {lead.lastName||''}</div>
      <div style={{ fontSize:11, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lead.company||lead.email}</div>
      <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
        {lead.crmValue>0      && <span style={{ fontSize:11, color:'#2ECC71', fontWeight:700 }}>€{lead.crmValue}</span>}
        {lead.score>0         && <span style={{ fontSize:10, color:lead.score>=20?'#2ECC71':lead.score>=10?'#F39C12':'#888', fontWeight:700, background:'var(--surface-3)', padding:'1px 6px', borderRadius:4 }}>⭐{lead.score}</span>}
        {lead.emailsOpened>0  && <span style={{ fontSize:10, color:'#3498DB', background:'rgba(52,152,219,0.1)', padding:'1px 6px', borderRadius:4 }}>👀{lead.emailsOpened}</span>}
        {lead.emailsReplied>0 && <span style={{ fontSize:10, color:'#2ECC71', background:'rgba(46,204,113,0.1)', padding:'1px 6px', borderRadius:4 }}>💬{lead.emailsReplied}</span>}
        {lead.crmNotes && <span style={{ fontSize:10, color:'#F39C12' }}>📝</span>}
      </div>
      <div style={{ display:'flex', gap:4, marginTop:10, flexWrap:'wrap' }} onClick={e=>e.stopPropagation()}>
        {STAGES.filter(s=>s!==(lead.crmStage||'new')).slice(0,3).map(s => (
          <button key={s} onClick={()=>moveStage(lead.id,s)}
            style={{ padding:'3px 8px', borderRadius:6, border:'1px solid var(--border-color)', background:'transparent', color:'var(--text-muted)', fontSize:10, cursor:'pointer' }}>
            {STAGE_LABEL[s]}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="CRM Pipeline"
        subtitle={`${leads.length} leads · €${totalValue.toLocaleString()} won · €${pipelineValue.toLocaleString()} in pipeline`}
        action={
          <div style={{ display:'flex', gap:8 }}>
            {['kanban','list'].map(v => (
              <button key={v} onClick={()=>setView(v)}
                style={{ padding:'7px 16px', borderRadius:8, border:`1px solid ${view===v?'#2ECC71':'var(--border-color)'}`, background:view===v?'rgba(46,204,113,0.1)':'transparent', color:view===v?'#2ECC71':'var(--text-muted)', fontSize:12, fontWeight:view===v?700:400, cursor:'pointer' }}>
                {v==='kanban'?'🗂 Kanban':'📋 List'}
              </button>
            ))}
          </div>
        }
      />

      {loading ? <div style={{ color:'var(--text-muted)', textAlign:'center', padding:40 }}>Loading...</div> : view === 'kanban' ? (
        <div style={{ display:'grid', gridTemplateColumns:`repeat(${STAGES.length},minmax(200px,1fr))`, gap:12, overflowX:'auto', padding:'4px 0 40px 0', minHeight:'70vh', width:'100%' }}>
          {STAGES.map(stage => (
            <div key={stage} style={{ minWidth:200, minHeight:'65vh' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, padding:'0 2px' }}>
                <Badge color={STAGE_COLOR[stage]}>{STAGE_LABEL[stage]}</Badge>
                <div style={{ fontSize:11, color:'var(--text-muted)' }}>{byStage(stage).length}</div>
              </div>
              <div style={{ minHeight:100 }}>
                {byStage(stage).map(l => <LeadCard key={l.id} lead={l} />)}
                {byStage(stage).length===0 && <div style={{ textAlign:'center', color:'var(--text-faint)', fontSize:12, padding:'20px 0' }}>Empty</div>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card style={{ padding:0, overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ background:'rgba(46,204,113,0.05)', borderBottom:'1px solid rgba(46,204,113,0.1)' }}>
                {['Name','Company','Email','Stage','Score','Value','Notes','Action'].map(h => (
                  <th key={h} style={{ padding:'11px 14px', textAlign:'left', color:'var(--text-muted)', fontWeight:700, fontSize:11, textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {leads.map(l => (
                  <tr key={l.id} style={{ borderBottom:'1px solid var(--border-subtle)' }}>
                    <td style={{ padding:'10px 14px', color:'var(--text-primary)', fontWeight:600 }}>{[l.firstName,l.lastName].filter(Boolean).join(' ')||'—'}</td>
                    <td style={{ padding:'10px 14px', color:'var(--text-secondary)' }}>{l.company||'—'}</td>
                    <td style={{ padding:'10px 14px', color:'#2ECC71' }}>{l.email}</td>
                    <td style={{ padding:'10px 14px' }}><Badge color={STAGE_COLOR[l.crmStage||'new']}>{STAGE_LABEL[l.crmStage||'new']}</Badge></td>
                    <td style={{ padding:'10px 14px', color:l.score>=20?'#2ECC71':l.score>=10?'#F39C12':'var(--text-muted)', fontWeight:700 }}>⭐{l.score||0}</td>
                    <td style={{ padding:'10px 14px', color:'#2ECC71', fontWeight:600 }}>{l.crmValue>0?`€${l.crmValue}`:'—'}</td>
                    <td style={{ padding:'10px 14px', color:'var(--text-secondary)', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.crmNotes||'—'}</td>
                    <td style={{ padding:'10px 14px' }}><Button size="sm" variant="ghost" onClick={()=>openNotes(l)}>📝 Notes</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={!!showNotes} onClose={()=>setShowNotes(null)} title={`Notes — ${showNotes?.firstName||''} ${showNotes?.lastName||showNotes?.email||''}`} size="md">
        {showNotes && (
          <div>
            <div style={{ background:'var(--surface-2)', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, border:'1px solid var(--border-color)' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {[['Email',showNotes.email],['Company',showNotes.company||'—'],['Score',`⭐${showNotes.score||0}`],['Sent',showNotes.emailsSent||0],['Opened',showNotes.emailsOpened||0],['Replied',showNotes.emailsReplied||0]].map(([l,v])=>(
                  <div key={l} style={{ display:'flex', justifyContent:'space-between' }}>
                    <span style={{ color:'var(--text-muted)' }}>{l}</span>
                    <span style={{ color:'var(--text-secondary)', fontWeight:500 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginBottom:6 }}>CRM Stage</label>
              <select value={showNotes.crmStage||'new'} onChange={e=>{setShowNotes(p=>({...p,crmStage:e.target.value}));moveStage(showNotes.id,e.target.value);}}>
                {STAGES.map(s=><option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
              </select>
            </div>
            <Input label="Deal Value (€)" type="number" placeholder="0" value={crmValue} onChange={e=>setCrmValue(e.target.value)} />
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginBottom:6 }}>Notes</label>
              <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={6}
                placeholder="Add notes about this lead — follow-up dates, key discussions, next steps..." />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <Button variant="secondary" onClick={()=>setShowNotes(null)} style={{ flex:1 }}>Cancel</Button>
              <Button loading={savingNotes} onClick={saveNotes} style={{ flex:2 }}>💾 Save Notes</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
